import { useEffect } from 'react';
import { getWorkflow } from 'Services/storeService';
import { message } from 'antd';
import { serializeGraph, setIdCounter } from '../constants';

/**
 * Loads the workflow structure (nodes + edges) from the API when a flowId is
 * present in the URL.
 *
 * Template CONTENT (emailContent, mjmlContent, smsContent) is intentionally
 * NOT loaded here — it lives exclusively in the template API and is fetched
 * on-demand by the email/SMS editor components. Keeping it out of ReactFlow
 * node state means template edits never touch flow state and never trigger
 * the auto-save interval.
 */
const useFlowLoader = ({
  flowId,
  setNodes,
  setEdges,
  setWorkflowId,
  setWorkflowVersion,
  setLastSavedVersion,
  setLastSavedAt,
  setWorkflowViewed,
  isFlowLoadedRef,
  lastSavedGraphRef,
  pendingSnapshotRef,
  setSaveStatus,
  setIsLoadingFlow,
  setWorkflowName,
  setFlowTypeCode,
  setAllowedNodes,
  setStructureLocked,
}) => {
  useEffect(() => {
    const loadFlow = async () => {
      if (flowId) {
        try {
          const response = await getWorkflow(flowId);
          const workflowData = response.data?.data;
          
          if (workflowData?.workflow?.name && typeof setWorkflowName === 'function') {
            setWorkflowName(workflowData.workflow.name);
          }

          if (typeof setFlowTypeCode === 'function') {
            setFlowTypeCode(workflowData?.workflow?.flow_type?.code ?? null);
          }

          if (typeof setAllowedNodes === 'function') {
            const allowed = workflowData?.workflow?.allowed_nodes;
            setAllowedNodes(Array.isArray(allowed) ? allowed : null);
          }

          if (typeof setStructureLocked === 'function') {
            setStructureLocked(Boolean(workflowData?.workflow?.flow_type?.structure_locked));
          }

          const workflowViewed = Boolean(
            workflowData?.workflow?.viewed ??
            workflowData?.viewed ??
            workflowData?.workflow?.active_version?.viewed
          );

          if (typeof setWorkflowViewed === 'function') {
            setWorkflowViewed(workflowViewed);
          }

          if (workflowData?.workflow?.id) {
            setWorkflowId(workflowData.workflow.id);
          }

          // Prefer draft over published version
          let graphData = null;
          let versionType = null;

          if (workflowData?.draft_version?.graph_json) {
            graphData = workflowData.draft_version.graph_json;
            versionType = 'draft';
            setLastSavedVersion(workflowData.draft_version.version_number);
            setLastSavedAt(new Date(workflowData.draft_version.updated_at));
          } else if (workflowData?.workflow?.active_version?.graph_json) {
            graphData = workflowData.workflow.active_version.graph_json;
            versionType = 'published';
            setLastSavedVersion(workflowData.workflow.active_version.version_number);
            setLastSavedAt(new Date(workflowData.workflow.active_version.updated_at));
          }

          setWorkflowVersion(versionType);

          if (graphData?.nodes) {
            setNodes(graphData.nodes || []);
            setEdges(graphData.edges || []);

            // Advance the ID counter so newly dropped nodes don't collide
            if (graphData.nodes.length > 0) {
              const maxId = Math.max(...graphData.nodes.map((n) => parseInt(n.id) || 0), 0);
              setIdCounter(maxId + 1);
            }

            // Snapshot the graph once React has processed the new nodes
            pendingSnapshotRef.current = true;
          }

          setSaveStatus('saved');
          isFlowLoadedRef.current = true;
        } catch (error) {
          console.error('Failed to load workflow:', error);
          message.error('Failed to load workflow');
        } finally {
          setIsLoadingFlow(false);
        }
        return;
      }

      // No flowId in URL — new empty flow
      if (typeof setWorkflowViewed === 'function') {
        setWorkflowViewed(false);
      }
      isFlowLoadedRef.current = true;
      setIsLoadingFlow(false);
    };

    loadFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);
};

export default useFlowLoader;
